import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';

import { messages as commonMessages } from '../../common/messages';
import { endpoints as scheduleEndpoints } from '../../services/endpoints/schedule';
import { saveTextAsFile } from '../../utils/save-text-as-file';

const useExportTimeline = (scheduleId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const exportTimeline = useCallback(async () => {
		setIsLoading(true);

		try {
			const { filename, content } = await scheduleEndpoints.exportTimeline({ scheduleId });

			saveTextAsFile(content, filename ?? 'file.ics');

			flagApi.showFlag({
				...successToastProps(),
				title: intlApi.formatMessage(commonMessages.exportTimelineSuccessTitle),
				isAutoDismiss: true,
			});
		} catch (error) {
			defaultCatch(
				{
					id: 'useExportTimeline-exportTimeline',
					httpErrorHandler: (error) => {
						const flagProps = error.message
							? { title: intlApi.formatMessage(commonMessages.error), description: error.message }
							: {
									title: intlApi.formatMessage(commonMessages.exportTimelineErrorTitle),
									actions: [
										{
											content: intlApi.formatMessage(commonMessages.tryAgain),
											onClick: exportTimeline,
										},
									],
								};
						flagApi.showFlag({ ...errorToastProps(), ...flagProps });
					},
				},
				error,
			);
		} finally {
			setIsLoading(false);
		}
	}, [defaultCatch, flagApi, intlApi, scheduleId]);

	return { isLoading, exportTimeline };
};

const useUpdateStatus = (scheduleId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const updateStatus = useCallback(
		async (enabled: boolean) => {
			setIsLoading(true);

			try {
				const data = await scheduleEndpoints.updateSchedule({ scheduleId, enabled });
				return Promise.resolve(data);
			} catch (error) {
				defaultCatch(
					{
						id: 'useUpdateStatus-updateStatus',
						httpErrorHandler: (error) => {
							const flagProps = error.message
								? {
										title: intlApi.formatMessage(commonMessages.error),
										description: error.message,
									}
								: {
										title: intlApi.formatMessage(commonMessages.updateScheduleErrorTitle),
										actions: [
											{
												content: intlApi.formatMessage(commonMessages.tryAgain),
												onClick: () => updateStatus(enabled),
											},
										],
									};
							flagApi.showFlag({ ...errorToastProps(), ...flagProps });
						},
					},
					error,
				);
				return Promise.reject(error);
			} finally {
				setIsLoading(false);
			}
		},
		[defaultCatch, flagApi, intlApi, scheduleId],
	);

	return { isLoading, updateStatus };
};

const useOpenWebCalendar = (scheduleId?: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const openWebCalendar = useCallback(async () => {
		setIsLoading(true);

		try {
			const data = await scheduleEndpoints.getWebCalendarUrl({ scheduleId });

			if (data) {
				window.open(data.url, '_blank');
			} else {
				throw new Error(intlApi.formatMessage(commonMessages.openWebCalendarErrorTitle));
			}
		} catch (error) {
			defaultCatch(
				{
					id: 'useOpenWebCalendar-openWebCalendar',
					httpErrorHandler: (error) => {
						const flagProps = error.message
							? {
									title: intlApi.formatMessage(commonMessages.error),
									description: error.message,
								}
							: {
									title: intlApi.formatMessage(commonMessages.openWebCalendarErrorTitle),
									actions: [
										{
											content: intlApi.formatMessage(commonMessages.tryAgain),
											onClick: openWebCalendar,
										},
									],
								};
						flagApi.showFlag({ ...errorToastProps(), ...flagProps });
					},
				},
				error,
			);
			return Promise.reject(error);
		} finally {
			setIsLoading(false);
		}
	}, [defaultCatch, flagApi, intlApi, scheduleId]);

	return { isLoading, openWebCalendar };
};

const useUpdateName = (scheduleId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const updateName = useCallback(
		async (name: string) => {
			setIsLoading(true);

			try {
				const data = await scheduleEndpoints.updateSchedule({ scheduleId, name });
				return Promise.resolve(data);
			} catch (error) {
				defaultCatch(
					{
						id: 'useUpdateName-updateName',
						httpErrorHandler: (error) => {
							const flagProps = error.message
								? {
										title: intlApi.formatMessage(commonMessages.error),
										description: error.message,
									}
								: {
										title: intlApi.formatMessage(commonMessages.updateScheduleErrorTitle),
										actions: [
											{
												content: intlApi.formatMessage(commonMessages.tryAgain),
												onClick: () => updateName(name),
											},
										],
									};
							flagApi.showFlag({ ...errorToastProps(), ...flagProps });
						},
					},
					error,
				);
				return Promise.reject(error);
			} finally {
				setIsLoading(false);
			}
		},
		[defaultCatch, flagApi, intlApi, scheduleId],
	);

	return { isLoading, updateName };
};

const useUpdateDescription = (scheduleId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const updateDescription = useCallback(
		async (description: string) => {
			setIsLoading(true);

			try {
				const data = await scheduleEndpoints.updateSchedule({ scheduleId, description });
				return Promise.resolve(data);
			} catch (error) {
				defaultCatch(
					{
						id: 'useUpdateDescription-updateDescription',
						httpErrorHandler: (error) => {
							const flagProps = error.message
								? {
										title: intlApi.formatMessage(commonMessages.error),
										description: error.message,
									}
								: {
										title: intlApi.formatMessage(commonMessages.updateScheduleErrorTitle),
										actions: [
											{
												content: intlApi.formatMessage(commonMessages.tryAgain),
												onClick: () => updateDescription(description),
											},
										],
									};
							flagApi.showFlag({ ...errorToastProps(), ...flagProps });
						},
					},
					error,
				);
				return Promise.reject(error);
			} finally {
				setIsLoading(false);
			}
		},
		[defaultCatch, flagApi, intlApi, scheduleId],
	);

	return { isLoading, updateDescription };
};

const useUpdateTimezone = (scheduleId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const updateTimezone = useCallback(
		async (timezone: string) => {
			setIsLoading(true);

			try {
				const data = await scheduleEndpoints.updateSchedule({ scheduleId, timezone });
				return Promise.resolve(data);
			} catch (error) {
				defaultCatch(
					{
						id: 'useUpdateTimezone-updateTimezone',
						httpErrorHandler: (error) => {
							const flagProps = error.message
								? {
										title: intlApi.formatMessage(commonMessages.error),
										description: error.message,
									}
								: {
										title: intlApi.formatMessage(commonMessages.updateScheduleErrorTitle),
										actions: [
											{
												content: intlApi.formatMessage(commonMessages.tryAgain),
												onClick: () => updateTimezone(timezone),
											},
										],
									};
							flagApi.showFlag({ ...errorToastProps(), ...flagProps });
						},
					},
					error,
				);
				return Promise.reject(error);
			} finally {
				setIsLoading(false);
			}
		},
		[defaultCatch, flagApi, intlApi, scheduleId],
	);

	return { isLoading, updateTimezone };
};

const useUpdateTeam = (scheduleId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const updateTeam = useCallback(
		async (teamId: string) => {
			setIsLoading(true);

			try {
				const data = await scheduleEndpoints.updateSchedule({
					scheduleId,
					teamId: teamId ?? '',
				});

				return Promise.resolve(data);
			} catch (error) {
				defaultCatch(
					{
						id: 'useUpdateTeam-updateTeam',
						httpErrorHandler: (error) => {
							const flagProps = error.message
								? {
										title: intlApi.formatMessage(commonMessages.error),
										description: error.message,
									}
								: {
										title: intlApi.formatMessage(commonMessages.updateScheduleErrorTitle),
										actions: [
											{
												content: intlApi.formatMessage(commonMessages.tryAgain),
												onClick: () => updateTeam(teamId),
											},
										],
									};
							flagApi.showFlag({ ...errorToastProps(), ...flagProps });
						},
					},
					error,
				);
				return Promise.reject(error);
			} finally {
				setIsLoading(false);
			}
		},
		[defaultCatch, flagApi, intlApi, scheduleId],
	);

	return { isLoading, updateTeam };
};

const useDeleteSchedule = (scheduleId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const deleteSchedule = useCallback(async () => {
		setIsLoading(true);

		try {
			const data = await scheduleEndpoints.deleteSchedule({ scheduleId });

			flagApi.showFlag({
				...successToastProps(),
				title: intlApi.formatMessage(commonMessages.deleteScheduleSuccessTitle),
				isAutoDismiss: true,
			});

			return Promise.resolve(data);
		} catch (error) {
			defaultCatch(
				{
					id: 'useDeleteSchedule-deleteSchedule',
					httpErrorHandler: (error) => {
						const flagProps = error.message
							? { title: intlApi.formatMessage(commonMessages.error), description: error.message }
							: {
									title: intlApi.formatMessage(commonMessages.deleteScheduleErrorTitle),
									isAutoDismiss: true,
								};
						flagApi.showFlag({ ...errorToastProps(), ...flagProps });
					},
				},
				error,
			);
			return Promise.reject(error);
		} finally {
			setIsLoading(false);
		}
	}, [defaultCatch, flagApi, intlApi, scheduleId]);

	return { isLoading, deleteSchedule };
};

export {
	useExportTimeline,
	useUpdateStatus,
	useUpdateName,
	useUpdateDescription,
	useUpdateTimezone,
	useUpdateTeam,
	useDeleteSchedule,
	useOpenWebCalendar,
};
